import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useMemo } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

type Profile = {
  id: string;
  username: string | null;
  display_name: string | null;
  avatar_url: string | null;
};

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signUp: (email: string, password: string, username: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signInWithGoogle: () => Promise<{ error: any }>;
  signInWithOTP: (email: string) => Promise<{ error: any }>;
  verifyOTP: (email: string, token: string) => Promise<{ error: any }>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  updatePassword: (password: string) => Promise<{ error: any }>;
  deleteAccount: () => Promise<{ error: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

// --- helpers ---

function slugify(input: string): string {
  return (input || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9._-]+/g, '-') // solo a-z 0-9 . _ -
    .replace(/^-+|-+$/g, '')
    .slice(0, 30);
}

function extractDisplayName(meta: Record<string, any> | undefined): string | null {
  if (!meta) return null;
  return (
    meta.preferred_username ||
    meta.user_name ||
    meta.name ||
    meta.full_name ||
    meta.given_name ||
    null
  );
}

// equality case-insensitive (niente wildcard)
async function usernameExists(candidate: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('profiles')
    .select('username')
    .ilike('username', candidate); // ilike senza % -> uguaglianza case-insensitive
  if (error) throw error;
  return (data ?? []).some(r => (r.username ?? '').toLowerCase() === candidate.toLowerCase());
}

async function findAvailableUsername(baseRaw: string): Promise<string> {
  let base = slugify(baseRaw);
  if (!base || base.includes('@') || base.length < 3) base = 'user';
  let suffix = 0;
  let candidate = base;
  while (await usernameExists(candidate)) {
    suffix += 1;
    candidate = `${base}-${suffix}`.slice(0, 30);
  }
  return candidate;
}

/**
 * Crea/aggiusta il profilo dell’utente:
 * - mai email come username
 * - usa metadata provider per display_name
 * - garantisce unicità username
 */
async function ensureProfile(user: User): Promise<Profile | null> {
  const { data: rows, error: selErr } = await supabase
    .from('profiles')
    .select('*')
    .eq('user_id', user.id)
    .limit(1);

  if (selErr) throw selErr;

  const existing = rows?.[0] as Profile | undefined;

  // Check if user has a desired username from registration
  const desiredUsername = (user.user_metadata as any)?.desired_username as string | undefined;
  const providerName = extractDisplayName(user.user_metadata) || '';

  // If profile exists, try to align with desiredUsername (if provided)
  if (existing) {
    const current = existing.username || '';
    const desiredValid = desiredUsername && desiredUsername.length >= 3 && !desiredUsername.includes('@');

    if (desiredValid && current.toLowerCase() !== desiredUsername!.toLowerCase()) {
      // Try to update to the desired username (ensure uniqueness)
      const safeDesired = await findAvailableUsername(desiredUsername!);
      const { data, error } = await supabase
        .from('profiles')
        .update({ username: safeDesired })
        .eq('user_id', user.id)
        .select()
        .single();
      if (error) throw error;
      return data as Profile;
    }

    // If existing username is valid (no '@'), keep it
    if (existing.username && !existing.username.includes('@')) {
      return existing;
    }
    // Otherwise fall-through to generate a safe username below
  }

  // No existing profile or invalid username: create/fix now
  const base = (desiredUsername && desiredUsername.length >= 3 && !desiredUsername.includes('@'))
    ? desiredUsername
    : (providerName && providerName.length >= 3 ? providerName : 'user');

  const safeUsername = await findAvailableUsername(base);

  const patch = {
    user_id: user.id,
    username: safeUsername,
    display_name: providerName || null,
    avatar_url: (user.user_metadata as any)?.avatar_url || (user.user_metadata as any)?.picture || null,
  };

  const { data, error } = await supabase.from('profiles').upsert(patch).select().single();
  if (error) throw error;
  return data as Profile;
}

// --- provider ---

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  // inizializzazione + listener
  useEffect(() => {
    // 1) Set up auth listener FIRST to not miss initial events
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      // Only synchronous state updates here to prevent deadlock
      setSession(session ?? null);
      setUser(session?.user ?? null);

      // Handle password recovery event: always send user to the reset form
      if (event === 'PASSWORD_RECOVERY') {
        setTimeout(() => {
          if (window.location.pathname !== '/reset-password') {
            window.location.replace('/reset-password');
          }
        }, 0);
      }

      // Defer async profile operations to prevent deadlock
      if (session?.user) {
        setTimeout(async () => {
          try {
            const p = await ensureProfile(session.user);
            setProfile(p);
          } catch (e: any) {
            console.error(e);
          }
        }, 0);
      } else {
        setProfile(null);
      }
    });

    // 2) THEN load any existing session
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session ?? null);
      setUser(data.session?.user ?? null);
      setLoading(false);

      if (data.session?.user) {
        // Defer profile loading to avoid blocking auth initialization
        setTimeout(async () => {
          try {
            const p = await ensureProfile(data.session.user);
            setProfile(p);
          } catch (e: any) {
            console.error(e);
          }
        }, 0);
      }
    });

    return () => {
      sub.subscription?.unsubscribe();
    };
  }, []);

  // --- actions ---

  const signUp = useCallback(async (email: string, password: string, username: string) => {
    const redirectUrl = `${window.location.origin}/`;
    const { error, data } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        // metadata utile per display_name, NON per username pubblico
        data: {
          display_name: username,
          desired_username: username, // Store the user's chosen username
        },
      },
    });

    if (error) {
      toast({ title: 'Errore di registrazione', description: error.message, variant: 'destructive' });
    } else {
      toast({
        title: 'Registrazione completata',
        description: 'Controlla la tua email per confermare l’account.',
      });
    }

    // Nota: l’upsert del profilo (con username scelto) avverrà quando la sessione sarà attiva.
    // Se vuoi forzarlo subito dopo la conferma, lascialo a ensureProfile.
    return { error };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      toast({ title: 'Errore di accesso', description: error.message, variant: 'destructive' });
    }
    return { error };
  }, []);

  const signInWithGoogle = useCallback(async () => {
    const redirectUrl = `${window.location.origin}/app`;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: redirectUrl },
    });
    if (error) {
      toast({ title: 'Errore di accesso', description: error.message, variant: 'destructive' });
    }
    return { error };
  }, []);

  const signInWithOTP = useCallback(async (email: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/otp-login`,
      }
    });
    if (error) {
      toast({ title: 'Errore invio OTP', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Codice inviato', description: 'Controlla la tua email per il codice OTP.' });
    }
    return { error };
  }, []);

  const verifyOTP = useCallback(async (email: string, token: string) => {
    const { error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email'
    });
    if (error) {
      toast({ title: 'Errore verifica', description: error.message, variant: 'destructive' });
    }
    return { error };
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) {
      toast({ title: 'Errore', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Email inviata', description: 'Controlla la tua email per il link di reset password.' });
    }
    return { error };
  }, []);

  const updatePassword = useCallback(async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      toast({ title: 'Errore', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Password aggiornata', description: 'La tua password è stata aggiornata con successo.' });
    }
    return { error };
  }, []);

  const deleteAccount = useCallback(async () => {
    if (!user) {
      toast({ title: 'Errore', description: 'Utente non trovato', variant: 'destructive' });
      return { error: new Error('Utente non trovato') };
    }

    try {
      console.log('Calling delete-user-account function...');
      
      const { data, error } = await supabase.functions.invoke('delete-user-account', {
        body: { userId: user.id }
      });

      if (error) {
        console.error('Edge function error:', error);
        const errorMsg = (data as any)?.error || error.message || 'Errore durante l\'eliminazione';
        toast({ 
          title: 'Errore eliminazione', 
          description: errorMsg, 
          variant: 'destructive' 
        });
        return { error: new Error(errorMsg) };
      }

      if (!data?.success) {
        const errorMsg = (data as any)?.error || 'Eliminazione fallita';
        console.error('Function returned error:', errorMsg);
        toast({ 
          title: 'Errore eliminazione', 
          description: errorMsg, 
          variant: 'destructive' 
        });
        return { error: new Error(errorMsg) };
      }

      console.log('Account deleted successfully, signing out...');
      
      // Sign out locally
      await supabase.auth.signOut();
      
      toast({ 
        title: 'Account eliminato', 
        description: 'Il tuo account è stato eliminato definitivamente.' 
      });
      
      return { error: null };
    } catch (error: any) {
      console.error('Unexpected error:', error);
      toast({ 
        title: 'Errore imprevisto', 
        description: error.message || 'Si è verificato un errore', 
        variant: 'destructive' 
      });
      return { error };
    }
  }, [user]);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast({ title: 'Errore', description: error.message, variant: 'destructive' });
    }
  }, []);

  const value = useMemo(
    () => ({ 
      user, 
      session, 
      profile, 
      loading, 
      signUp, 
      signIn, 
      signInWithGoogle, 
      signInWithOTP,
      verifyOTP,
      resetPassword, 
      updatePassword,
      deleteAccount,
      signOut 
    }),
    [user, session, profile, loading, signUp, signIn, signInWithGoogle, signInWithOTP, verifyOTP, resetPassword, updatePassword, deleteAccount, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
